import React, { useState, useEffect, useRef, useId, Suspense } from "react";
import * as R from "ramda";
import { string, array, shape, bool } from "prop-types";
import useSearchValues from "../../common/useSearchValues.js";
import useFetchJson from "../../common/useFetchJson.js";
import {
  scrollToResults,
  metaParser,
  toggleFacet,
} from "../../common/helpers.js";
import useEvent from "../../common/useEvent.js";
import cx from "classnames";
import Placeholder from "../Placeholder/Placeholder.jsx";
import "./searchApp.scss";
import useHashTrigger from "../../common/useHashTrigger.js";
const ResultItem = React.lazy(() => import("../ResultItem/ResultItem.jsx"));
const ViewBtns = React.lazy(() => import("../ViewBtns/ViewBtns.jsx"));
const Pagination = React.lazy(() => import("../Pagination/Pagination.jsx"));
const Sorters = React.lazy(() => import("../Sorters/Sorters.jsx"));
const ResultsInfo = React.lazy(() => import("../ResultsInfo/ResultsInfo.jsx"));
const SearchError = React.lazy(() => import("../../views/SearchError.jsx"));
const SelectedFilters = React.lazy(() =>
  import("../SelectedFilters/SelectedFilters.jsx"),
);
const KeywordSearch = React.lazy(() =>
  import("../KeywordSearch/KeywordSearch.jsx"),
);
const SubGroupSelect = React.lazy(() =>
  import("../SubGroupSelect/SubGroupSelect.jsx"),
);

//if the key for results is not "results" it can be updated without modifying code.
const key_results = import.meta.env.VITE_RESULTS;
// const key_filters = import.meta.env.VITE_FILTERS;

const propTypes = {
  model: shape({
    api: string.isRequired,
    dom: array.isRequired,
    loggedIn:bool
  }),
};

const SearchApp = (props) => {
  const { queryStringState, setQueryStringState} = props;
  const { api, dom, loggedIn, viewmode } = props.model;
  const id = useId();

  // console.log("searchAppprops,",props);
  const customEv = useEvent("newResults", () =>
    scrollToResults(".search__results"),
  );
  const { apiQueryString, searchValues } = useSearchValues();
  const page = useRef(0);
  const response = useFetchJson(`${api}${apiQueryString}`, {
    credentials: "same-origin",
    method: "GET",
  });

  const serverData = response?.data;
  const searchResults = serverData?.[key_results];
  // const searchFilters = serverData?.[key_filters];
  const hasResults = serverData?.[key_results]?.length > 0;
  const pager = serverData?.pager;
  const totalResults = Number(serverData?.pager?.total_results);
  // const totalPages = Number(serverData?.pager?.total_pages);
  // const resultsPerPage = serverData?.pager?.items_per_page || perPage || 6;
  const isLoading = response?.isLoading;
  const [initial, setInitial] = useState(true);
  const [resultView, setResultView] = useState(viewmode === 'table');
   const parsedFacets = metaParser(serverData);
   const dlTrigger = useHashTrigger();

   useEffect(() => {
    /*
    if the page arg has changed and this is not the
    initial load dispatch the new page load event.
    */
    if (searchValues.page != page.current && !initial) {
      page.current = searchValues.page;
      customEv.dispatchEv();
    } else {
      //wait 5 seconds before triggering download
      setTimeout(()=>dlTrigger.triggerDownload(),4000);
    }
    setInitial(false);
  }, [searchValues.page]);

  const handleFacetChange = (param, facetValue, selected) => {
    setQueryStringState({
      ...toggleFacet(param, selected.facet_value, queryStringState),
      page: 0,
    });
  };


  //consider the following { 'foo': <Foo />, 'bar': <Bar /> }[param] || <Baz />
  const renderSwitch = (itemId) => {
    switch (itemId) {
      case "filter-mount":
        return parsedFacets?.length > 0 ? (
          <div className="search__filter-bar machine-render">
            <SubGroupSelect
              key={JSON.stringify(parsedFacets)}
              placeholder={"Filter by"}
              label={"Filter by"}
              className="select--grouped"
              itemToId={R.prop("field_id")}
              handleFacetChange={handleFacetChange}
              onClear={() => setQueryStringState({ f: [], page: 0 })}
              queryStringState={queryStringState}
              setQueryStringState={setQueryStringState}
              selectProps={{
                items: parsedFacets,
                itemToString: R.prop("label"),
              }}
            />
          </div>
        ) : null;
      case "keyword-mount":
        return (
          <KeywordSearch
            typeaheadApi={serverData?.system?.typeahead}
            classNames="search__input"
            keywords={String(queryStringState?.keyword || "")}
            handleEntry={(value) =>
              setQueryStringState({ keyword: value, page: 0 })
            }
            dictionary={{
              searchMinLengthMessage:
                "Search requires a minimum of 3 characters",
              searchBoxPlaceholder: "Search",
              searchBtnText: "Search",
            }}
            inputOptions={{
              type: "text",
              id: "search",
              name: "search",
              minLength: "3",
            }}
          />
        );
      case "sort-mount":
        return (
          <Sorters
            sorters={serverData.sorters}
            onSortChange={setQueryStringState}
          />
        );
      case "view-opt-mount":
        return <ViewBtns currentState={resultView} handleChangeView={setResultView} />;
      case "selected-filters-mount":
        return (
          <SelectedFilters
            filters={parsedFacets}
            queryStringState={queryStringState}
            onFacetChange={setQueryStringState}
          />
        );
      case "meta-mount":
        return serverData?.pager?.total_results > 0 ? (
          <div className="search__results-info">
            <ResultsInfo
              totalResults={Number(serverData?.pager?.total_results)}
              isLoading={response?.isLoading}
              perPage={Number(serverData?.pager?.items_per_page)}
              keyword={searchValues?.keyword}
              page={Number(searchValues?.page)}
              id="resultInfo"
            />
          </div>
        ) : null;
      case "result-mount":
        return hasResults ? (
          <div
            className={cx("search__results", "section", {
              "search__results--grid": resultView,
              "search__results--list": !resultView,
              "search__results--loading": isLoading,
            })}
            id={`${id}-results`}
            role="region"
            tabIndex="-1"
            aria-labelledby="resultInfo"
          >
            {searchResults.map((result, i) => {
               return (
                <ResultItem
                  result={result}
                  key={`result-${result.nid}-${i}`}
                  className="search__result-item coh-style-search-card"
                  loggedIn={loggedIn}
                />
              );
            })}
          </div>
        ) : (
          "No results found."
        );
      case "pagination-mount":
        return hasResults && totalResults ? (
          <Pagination pager={pager} onPageChange={setQueryStringState} />
        ) : null;
      default:
        return false;
    }
  };

  const renderLayout = (arr) =>
    arr.map((item, i) => (
      <div className={item.className} id={item.id} key={`rendered-${i}`}>
        <div className="item-content">{item.value}</div>
        {renderSwitch(item.id)}
        {item?.childNodes?.length > 0 && renderLayout(item.childNodes)}
      </div>
    ));

  return response.error ? (
    <Suspense fallback={<Placeholder />}>
      <SearchError text={"Whoops! somethings gone wrong."} />
    </Suspense>
  ) : serverData ? (
    <Suspense fallback={<Placeholder />}>{renderLayout(dom)}</Suspense>
  ) : (
    <Placeholder />
  );
};

SearchApp.propTypes = propTypes;
export default SearchApp;
